import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { BACKEND_URL } from '../../../config';
import { useOrganization } from '../../../context/OrganizationContext';

import {
  ArrowCounterClockwise,
  ArrowSquareOut,
  CaretDown,
  CaretUp,
  CheckCircle,
  CircleNotch,
  Clock,
  ClockCounterClockwise,
  Copy,
  CurrencyDollar,
  Eye,
  Lightning,
  Play,
  WarningCircle,
  XCircle
} from '@phosphor-icons/react';
/**
 * RunHistoryPanel - Enhanced run history with replay capability
 * Shows recent runs for the current agent with input/output replay
 */
const RunHistoryPanel = ({ agentId, isExpanded, onToggle, onReplayRun }) => {
  const { activeOrganization } = useOrganization();
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRun, setSelectedRun] = useState(null);
  const [runDetails, setRunDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  useEffect(() => {
    if (agentId && isExpanded) {
      fetchRuns();
    }
  }, [agentId, isExpanded]);

  const fetchRuns = async () => {
    if (!agentId || !activeOrganization?.organization_id) return;
    
    setLoading(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/flow/runs/${agentId}?limit=10`,
        {
          credentials: 'include',
          headers: { 'X-Organization-Id': activeOrganization.organization_id }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setRuns(data.runs || []);
      }
    } catch (err) {
      console.error('Error fetching runs:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRunDetails = async (runId) => {
    if (!runId || !activeOrganization?.organization_id) return;
    
    setLoadingDetails(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/flow/run/${runId}`,
        {
          credentials: 'include',
          headers: { 'X-Organization-Id': activeOrganization.organization_id }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setRunDetails(data);
      }
    } catch (err) {
      console.error('Error fetching run details:', err);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleRunSelect = (runId) => {
    if (selectedRun === runId) {
      setSelectedRun(null);
      setRunDetails(null);
    } else {
      setSelectedRun(runId);
      fetchRunDetails(runId);
    }
  };

  const handleReplay = (run) => {
    if (onReplayRun && run.input_data) {
      onReplayRun(run.input_data);
      toast.success('Input loaded - click Run Test to replay');
    }
  };

  const handleCopyOutput = (output) => {
    const text = typeof output === 'object' 
      ? JSON.stringify(output, null, 2) 
      : String(output);
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
      case 'success':
        return <CheckCircle size={14} className="text-emerald-400" />;
      case 'failed':
      case 'error':
        return <XCircle size={14} className="text-red-400" />;
      case 'running':
        return <CircleNotch size={14} className="text-blue-400 animate-spin" />;
      default:
        return <WarningCircle size={14} className="text-amber-400" />;
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleDateString();
  };

  const getInputPreview = (inputData) => {
    if (!inputData) return 'No input';
    if (typeof inputData === 'string') return inputData.substring(0, 50);
    if (inputData.textInput) return inputData.textInput.substring(0, 50);
    return JSON.stringify(inputData).substring(0, 50);
  };

  if (!agentId) {
    return null;
  }

  return (
    <div className="border-t border-white/10 bg-[#0d0e12]">
      {/* Header */}
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-white/5 transition-colors"
        data-testid="run-history-toggle"
      >
        <div className="flex items-center gap-2">
          <ClockCounterClockwise size={14} className="text-slate-400" />
          <span className="text-sm font-medium text-white">Run ClockCounterClockwise</span>
          {runs.length > 0 && (
            <span className="px-1.5 py-0.5 rounded text-[10px] bg-white/10 text-slate-400">
              {runs.length}
            </span>
          )}
        </div>
        {isExpanded ? (
          <CaretDown size={16} className="text-slate-500" />
        ) : (
          <CaretUp size={16} className="text-slate-500" />
        )}
      </button>

      {/* Content */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-3 max-h-64 overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center py-4">
                  <CircleNotch size={20} className="text-indigo-400 animate-spin" />
                </div>
              ) : runs.length === 0 ? (
                <div className="text-center py-4">
                  <ClockCounterClockwise size={24} className="mx-auto text-slate-600 mb-2" />
                  <p className="text-xs text-slate-500">No runs yet</p>
                  <p className="text-[10px] text-slate-600">Run a test to see history here</p>
                </div>
              ) : (
                <div className="space-y-1.5">
                  {runs.map(run => (
                    <div key={run.run_id || run.id}>
                      <div
                        onClick={() => handleRunSelect(run.run_id || run.id)}
                        className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                          selectedRun === (run.run_id || run.id)
                            ? 'bg-indigo-500/20 border border-indigo-500/30' 
                            : 'bg-white/5 hover:bg-white/10 border border-transparent'
                        }`}
                        data-testid={`run-item-${run.run_id || run.id}`}
                      >
                        {getStatusIcon(run.status || (run.success ? 'success' : 'error'))}
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-white truncate">
                            {getInputPreview(run.input_data) || 'Agent run'}
                          </p>
                          <p className="text-[10px] text-slate-500">
                            {formatTime(run.created_at || run.started_at)}
                          </p>
                        </div>
                        {run.metrics?.duration && (
                          <span className="text-[10px] text-slate-500">
                            {run.metrics.duration}
                          </span>
                        )}
                      </div>

                      {/* Expanded Run Details */}
                      <AnimatePresence>
                        {selectedRun === (run.run_id || run.id) && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="mt-2 p-3 rounded-lg bg-white/5 border border-white/10 space-y-3"
                          >
                            {loadingDetails ? (
                              <div className="flex items-center justify-center py-2">
                                <CircleNotch size={16} className="text-indigo-400 animate-spin" />
                              </div>
                            ) : (
                              <>
                                {/* Metrics Row */}
                                {(runDetails?.metrics || run.metrics) && (
                                  <div className="flex flex-wrap items-center gap-3 text-[10px]">
                                    {(runDetails?.metrics?.estimated_tokens || run.metrics?.tokens) && (
                                      <div className="flex items-center gap-1">
                                        <Lightning size={10} className="text-amber-400" />
                                        <span className="text-slate-400">
                                          {runDetails?.metrics?.estimated_tokens || run.metrics?.tokens} tokens
                                        </span>
                                      </div>
                                    )}
                                    {(runDetails?.metrics?.estimated_cost || run.metrics?.cost) && (
                                      <div className="flex items-center gap-1">
                                        <CurrencyDollar size={10} className="text-emerald-400" />
                                        <span className="text-slate-400">
                                          ${(runDetails?.metrics?.estimated_cost || 0).toFixed(4)}
                                        </span>
                                      </div>
                                    )}
                                    {(runDetails?.metrics?.nodes_completed || run.metrics?.nodesExecuted) && (
                                      <div className="flex items-center gap-1">
                                        <CheckCircle size={10} className="text-purple-400" />
                                        <span className="text-slate-400">
                                          {runDetails?.metrics?.nodes_completed || run.metrics?.nodesExecuted} nodes
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                )}

                                {/* Input Section */}
                                {(runDetails?.input_data || run.input_data) && (
                                  <div className="space-y-1">
                                    <div className="flex items-center justify-between">
                                      <span className="text-[10px] text-slate-500 uppercase tracking-wider">Input</span>
                                      <button
                                        onClick={() => handleReplay(runDetails || run)}
                                        className="flex items-center gap-1 text-[10px] text-indigo-400 hover:text-indigo-300"
                                        data-testid="replay-run-btn"
                                      >
                                        <Play size={10} />
                                        Replay
                                      </button>
                                    </div>
                                    <div className="p-2 rounded bg-indigo-500/10 border border-indigo-500/20">
                                      <p className="text-xs text-slate-300 line-clamp-2">
                                        {getInputPreview(runDetails?.input_data || run.input_data)}...
                                      </p>
                                    </div>
                                  </div>
                                )}

                                {/* Output Section */}
                                {(runDetails?.output || run.output) && (
                                  <div className="space-y-1">
                                    <div className="flex items-center justify-between">
                                      <span className="text-[10px] text-slate-500 uppercase tracking-wider">Output</span>
                                      <button
                                        onClick={() => handleCopyOutput(runDetails?.output || run.output)}
                                        className="flex items-center gap-1 text-[10px] text-slate-400 hover:text-white"
                                      >
                                        <Copy size={10} />
                                        Copy
                                      </button>
                                    </div>
                                    <div className="p-2 rounded bg-emerald-500/10 border border-emerald-500/20">
                                      <p className="text-xs text-slate-300 line-clamp-3">
                                        {typeof (runDetails?.output || run.output) === 'object'
                                          ? JSON.stringify(runDetails?.output || run.output).substring(0, 150)
                                          : String(runDetails?.output || run.output).substring(0, 150)}...
                                      </p>
                                    </div>
                                  </div>
                                )}

                                {/* Error Section */}
                                {(runDetails?.error || run.error) && (
                                  <div className="p-2 rounded bg-red-500/10 border border-red-500/20">
                                    <p className="text-xs text-red-400">
                                      {runDetails?.error || run.error}
                                    </p>
                                  </div>
                                )}

                                {/* Run ID */}
                                <div className="flex items-center justify-between pt-1 border-t border-white/5">
                                  <span className="text-[9px] text-slate-600 font-mono">
                                    {run.run_id || run.id}
                                  </span>
                                </div>
                              </>
                            )}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ))}
                </div>
              )}

              {/* Refresh Button */}
              {runs.length > 0 && (
                <button
                  onClick={fetchRuns}
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-1 text-xs text-slate-500 hover:text-indigo-400 mt-3 py-1"
                >
                  <ArrowCounterClockwise size={12} className={loading ? 'animate-spin' : ''} />
                  Refresh
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default RunHistoryPanel;
